import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";
import withAuth from "../../utils/withAuth";
import { fetchCallAllDataByIDAPI } from "../../utils/api";
import { numberFormatWithGst } from "../../components/numberFormatWithGst";

const HOURS = Array.from({ length: 24 }, (_, i) => i + 1);

// Highlight thresholds
const PERF_GREEN_ABOVE = 15; // %
const COST_RED_ABOVE = 100;  // currency (Per Call)

// date helper (local)
const getTodayYYYYMMDD = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const getPreviousDateYYYYMMDD = (dateStr) => {
    const d = new Date(`${dateStr}T00:00:00`);
    d.setDate(d.getDate() - 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

// Skeleton helpers
const SkeletonLine = ({ w = "100%", h = 14, mb = 8 }) => (
    <div className="sk" style={{ width: w, height: h, marginBottom: mb }} />
);

const HourlyTableSkeleton = ({ rows = 6 }) => (
    <>
        {Array.from({ length: rows }).map((_, i) => (
            <tr key={`hsk-${i}`}>
                <td><SkeletonLine w="55px" h={12} mb={0} /></td>
                <td style={{ textAlign: "right" }}><SkeletonLine w="70px" h={12} mb={0} /></td>
                <td style={{ textAlign: "right" }}><SkeletonLine w="70px" h={12} mb={0} /></td>
                <td style={{ textAlign: "right" }}><SkeletonLine w="70px" h={12} mb={0} /></td>
                <td style={{ textAlign: "right" }}><SkeletonLine w="90px" h={12} mb={0} /></td>
                <td style={{ textAlign: "right" }}><SkeletonLine w="80px" h={12} mb={0} /></td>
                <td style={{ textAlign: "right" }}><SkeletonLine w="80px" h={12} mb={0} /></td>
                <td style={{ textAlign: "right" }}><SkeletonLine w="55px" h={12} mb={0} /></td>
            </tr>
        ))}
    </>
);

const perCallCost = (spend, calls) => {
    const c = Number(calls || 0);
    const s = Number(spend || 0);
    if (c <= 0) return 0;
    return s / c;
};

const PPC33_ALLOWED_SKILLS = new Set(["Z_HIN_CONSULTATION11_SKS", "X_HIN_AK61_SKS"]);

const filterPpc33Rows = (rows, enabled) => {
    if (!enabled) return Array.isArray(rows) ? rows : [];
    return (Array.isArray(rows) ? rows : []).filter((row) =>
        PPC33_ALLOWED_SKILLS.has(String(row?.skillname || "").trim())
    );
};

const buildHourlyAggregate = (rows) => {
    const hourMap = new Map();
    let totalCalls = 0;
    let totalOrders = 0;
    let totalSpend = 0;

    (Array.isArray(rows) ? rows : []).forEach((r) => {
        const calls = Number(r?.total_calls || 0);
        const orders = Number(r?.total_orders || 0);
        const spend = Number(r?.total_spend || 0);

        totalCalls += calls;
        totalOrders += orders;
        totalSpend += spend;

        (r.hours || []).forEach((h) => {
            const hr = Number(h?.hour || 0);
            if (!hr) return;
            const prev = hourMap.get(hr) || { hour: hr, calls: 0, orders: 0, spend: 0, cpc_spend: 0, cpc_clicks: 0, perf: 0 };
            prev.calls += Number(h?.calls || 0);
            prev.orders += Number(h?.orders || 0);
            prev.spend += Number(h?.spend || 0);
            prev.cpc_spend += Number(h?.cpc_spend || 0);
            prev.cpc_clicks += Number(h?.cpc_clicks || 0);
            hourMap.set(hr, prev);
        });
    });

    const hours = Array.from(hourMap.values())
        .sort((a, b) => a.hour - b.hour)
        .map((x) => ({
            ...x,
            spend: Number(x.spend || 0),
            cpc: Number(x.cpc_clicks || 0) > 0 ? round2(Number(x.cpc_spend || 0) / Number(x.cpc_clicks || 0)) : 0,
            perf: Number(x.calls || 0) > 0 ? Math.round(((Number(x.orders || 0) / Number(x.calls || 0)) * 100) * 10) / 10 : 0,
        }));

    const summary = {
        total_calls: totalCalls,
        total_orders: totalOrders,
        total_spend: round2(totalSpend),
        perf: totalCalls > 0 ? Math.round(((totalOrders / totalCalls) * 100) * 10) / 10 : 0,
    };

    return { summary, hours };
};

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

const SkillCardSkeleton = () => (
    <div className="col-xl-4 col-lg-6 col-md-6 mb-3">
        <div className="live-center-card">
            <div className="live-center-card-head">
                <div className="sh-left"><SkeletonLine w="120px" h={16} mb={0} /></div>
                <div className="live-center-metrics">
                    <SkeletonLine w="180px" h={28} mb={0} />
                </div>
            </div>

            <div className="live-table-scroll">
                <table className="table mb-0 mini-table live-skill-table table-bordred">
                        <thead>
                            <tr className="colHead">
                                <th style={{ width: 90 }}>Hour</th>
                                <th style={{ width: 120, textAlign: "right" }}>TCalls</th>
                                <th style={{ width: 120, textAlign: "right" }}>YCalls</th>
                                <th style={{ width: 120, textAlign: "right" }}>Orders</th>
                                <th style={{ width: 140, textAlign: "right" }}>Spend</th>
                                <th style={{ width: 110, textAlign: "right" }}>CPC</th>
                                <th style={{ width: 140, textAlign: "right" }}>Per Call</th>
                                <th style={{ width: 100, textAlign: "right" }}>Perf</th>
                            </tr>
                        </thead>

                        <tbody>
                            <HourlyTableSkeleton rows={6} />
                        </tbody>
                    </table>
            </div>
        </div>
    </div>
);

const HourlyPPCTodayReport = () => {
    const [rows, setRows] = useState([]);
    const [hours, setHours] = useState([]);
    const [summary, setSummary] = useState(null);
    const [yesterdayRows, setYesterdayRows] = useState([]);
    const [yesterdayHours, setYesterdayHours] = useState([]);
    const [yesterdaySummary, setYesterdaySummary] = useState(null);
    const [meta, setMeta] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [showSkills, setShowSkills] = useState(true);

    // Range states
    const [startHour, setStartHour] = useState(1);
    const [endHour, setEndHour] = useState(1);
    const [appliedRange, setAppliedRange] = useState({ start: 1, end: 1 });
    const [applyTick, setApplyTick] = useState(0);

    // Date filter
    const [selectedDate, setSelectedDate] = useState(getTodayYYYYMMDD());
    const [appliedDate, setAppliedDate] = useState(getTodayYYYYMMDD());

    // Skill filter states
    const [skillOpen, setSkillOpen] = useState(false);
    const [skillSearch, setSkillSearch] = useState("");
    const [selectedSkills, setSelectedSkills] = useState(new Set());
    const [appliedSkills, setAppliedSkills] = useState(new Set());
    const skillRef = useRef(null);
    const reportCacheRef = useRef(new Map());

    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");
    const isPpc33 = Number(ppcid) === 33;
    const visibleRows = useMemo(() => {
        const baseRows = filterPpc33Rows(rows, isPpc33);
        if (!isPpc33) return baseRows;

        const existing = new Set(baseRows.map((row) => String(row?.skillname || "").trim()));
        const missing = Array.from(PPC33_ALLOWED_SKILLS).filter((skill) => !existing.has(skill));
        const sh = Number(meta?.start_hour ?? appliedRange?.start ?? 1) || 1;
        const eh = Number(meta?.end_hour ?? appliedRange?.end ?? sh) || sh;
        const seedHours = Array.from({ length: Math.max(1, eh - sh + 1) }, (_, idx) => ({
            hour: sh + idx,
            calls: 0,
            orders: 0,
            spend: 0,
            perf: 0,
        }));

        const seedRows = missing.map((skill) => ({
            skillname: skill,
            process_mobile: "",
            total_calls: 0,
            total_orders: 0,
            total_spend: 0,
            team_name: "",
            hours: seedHours,
        }));

        return [...baseRows, ...seedRows];
    }, [rows, isPpc33, meta, appliedRange]);

    const visibleYesterdayRows = useMemo(() => {
        const baseRows = filterPpc33Rows(yesterdayRows, isPpc33);
        if (!isPpc33) return baseRows;

        const existing = new Set(baseRows.map((row) => String(row?.skillname || "").trim()));
        const missing = Array.from(PPC33_ALLOWED_SKILLS).filter((skill) => !existing.has(skill));
        const seedHours = Array.from({ length: 24 }, (_, idx) => ({
            hour: idx + 1,
            calls: 0,
            orders: 0,
            spend: 0,
            perf: 0,
        }));
        const seedRows = missing.map((skill) => ({
            skillname: skill,
            process_mobile: "",
            total_calls: 0,
            total_orders: 0,
            total_spend: 0,
            team_name: "",
            hours: seedHours,
        }));

        return [...baseRows, ...seedRows];
    }, [yesterdayRows, isPpc33]);
    const visibleCurrentAggregate = useMemo(
        () => (isPpc33 ? buildHourlyAggregate(visibleRows) : null),
        [isPpc33, visibleRows]
    );
    const visibleYesterdayAggregate = useMemo(
        () => (isPpc33 ? buildHourlyAggregate(visibleYesterdayRows) : null),
        [isPpc33, visibleYesterdayRows]
    );

    // Default = current hour only
    useEffect(() => {
        const now = new Date();
        const h24 = now.getHours(); // 0-23
        const currentHour = h24 === 0 ? 24 : h24;

        setStartHour(currentHour);
        setEndHour(currentHour);
        setAppliedRange({ start: currentHour, end: currentHour });
    }, []);

    useEffect(() => {
        if (!ppcid) return navigate("/");
        navigate("/hourly-today-report");
    }, [ppcid]);

    // Close skill dropdown on outside click
    useEffect(() => {
        const onDoc = (e) => {
            if (!skillRef.current) return;
            if (!skillRef.current.contains(e.target)) setSkillOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    // Load when appliedRange/appliedDate changes
    useEffect(() => {
        if (!ppcid) return;
        if (!appliedRange?.start || !appliedRange?.end) return;
        if (!appliedDate) return;
        load(ppcid, appliedRange.start, appliedRange.end, appliedDate, false, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid, appliedRange, appliedDate, applyTick]);

    const getReportCacheKey = (id, sh, eh, dateStr) => `${id}|${dateStr}|${sh}|${eh}`;

    const applyReportPayload = (p, sh, eh, dateStr, cacheTime = new Date(), y = null) => {
        const newRows = Array.isArray(p?.rows) ? p.rows : [];
        const yRows = Array.isArray(y?.rows) ? y.rows : [];

        setRows(newRows);
        setHours(Array.isArray(p?.hours) ? p.hours : []);
        setSummary(p?.summary || null);
        setYesterdayRows(yRows);
        setYesterdayHours(Array.isArray(y?.hours) ? y.hours : []);
        setYesterdaySummary(y?.summary || null);
        setMeta(p?.meta || { start_hour: sh, end_hour: eh, date: dateStr });
        setLastUpdatedAt(cacheTime);

        setSelectedSkills(new Set());
        setAppliedSkills(new Set());
    };

    const load = async (id, sh, eh, dateStr, silent = false, forceRefresh = false) => {
        const cacheKey = getReportCacheKey(id, sh, eh, dateStr);
        const cached = reportCacheRef.current.get(cacheKey);
        if (!forceRefresh && cached?.payload) {
            applyReportPayload(cached.payload, sh, eh, dateStr, cached.cachedAt || new Date(), cached.yesterdayPayload || null);
            return;
        }

        if (!silent) setIsLoading(true);
        try {
            const refreshKey = Date.now().toString();
            const makeUrl = (d) => {
                const refreshParam = forceRefresh ? `&_refresh=${refreshKey}` : "";
                return `ppc-team-get-today-live-call-hour-wise-with-proccess-name-by-id/${id}?start_hour=${sh}&end_hour=${eh}&date=${encodeURIComponent(d)}${refreshParam}`;
            };
            const res = await fetchCallAllDataByIDAPI(makeUrl(dateStr));
            const yDate = getPreviousDateYYYYMMDD(dateStr);
            const yRes = await fetchCallAllDataByIDAPI(makeUrl(yDate));

            if (res?.data?.status === 1) {
                const p = res?.data?.process || {};
                const y = yRes?.data?.status === 1 ? (yRes?.data?.process || {}) : {};
                const cachedAt = new Date();
                reportCacheRef.current.set(cacheKey, { payload: p, yesterdayPayload: y, cachedAt });
                const newRows = Array.isArray(p?.rows) ? p.rows : [];
                const yRows = Array.isArray(y?.rows) ? y.rows : [];

                setRows(newRows);
                setHours(Array.isArray(p?.hours) ? p.hours : []);
                setSummary(p?.summary || null);
                setYesterdayRows(yRows);
                setYesterdayHours(Array.isArray(y?.hours) ? y.hours : []);
                setYesterdaySummary(y?.summary || null);
                setMeta(p?.meta || { start_hour: sh, end_hour: eh, date: dateStr });
                setLastUpdatedAt(new Date());

                setSelectedSkills(new Set());
                setAppliedSkills(new Set());
            } else {
                toast.error(res?.data?.message || "Failed to load hourly report");
                setRows([]);
                setHours([]);
                setSummary(null);
                setYesterdayRows([]);
                setYesterdayHours([]);
                setYesterdaySummary(null);
                setMeta(null);
            }
        } catch (e) {
            console.error(e);
            toast.error("Network/CORS error while loading hourly report");
            setRows([]);
            setHours([]);
            setSummary(null);
            setYesterdayRows([]);
            setYesterdayHours([]);
            setYesterdaySummary(null);
            setMeta(null);
        } finally {
            if (!silent) setIsLoading(false);
        }
    };

    // ✅ Apply date + range button
    const onApply = () => {
        const sh = Number(startHour);
        const eh = Number(endHour);

        if (!selectedDate) return toast.error("Please select a date");
        if (!sh || !eh) return toast.error("Please select both From & To hour");
        if (sh > eh) return toast.error("From hour cannot be greater than To hour");

        setAppliedDate(selectedDate);
        setAppliedRange({ start: sh, end: eh });
        setApplyTick((v) => v + 1);
    };

    // ✅ Skill options
    const onRefresh = () => {
        if (!ppcid) return;
        if (!appliedDate || !appliedRange?.start || !appliedRange?.end) return;
        load(ppcid, appliedRange.start, appliedRange.end, appliedDate, false, true);
    };

    const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
    const showProcessMobile = Number(ppcid) === 31;
    const processMobileBySkill = useMemo(() => {
        const map = {};
        visibleRows.forEach((r) => {
            const skill = String(r?.skillname || "").trim();
            const mobile = String(r?.process_mobile || "").trim();
            if (skill && mobile) map[skill] = mobile;
        });
        return map;
    }, [visibleRows]);
    const getSkillDisplay = (skillOrRow) => {
        const skill = typeof skillOrRow === "string"
            ? skillOrRow
            : String(skillOrRow?.skillname || "").trim();
        const rowMobile = typeof skillOrRow === "string"
            ? ""
            : String(skillOrRow?.process_mobile || "").trim();
        const mobile = rowMobile || processMobileBySkill[skill] || "";
        return showProcessMobile && mobile ? mobile : skill;
    };

    const skillOptions = useMemo(() => {
        const names = visibleRows
            .filter((x) => !isPpc33 || Number(x?.total_calls || 0) >= 0)
            .map((x) => String(x?.skillname || "").trim())
            .filter(Boolean);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
    }, [visibleRows, isPpc33]);

    const filteredSkillOptions = useMemo(() => {
        const q = skillSearch.trim().toLowerCase();
        if (!q) return skillOptions;
        return skillOptions.filter((s) =>
            s.toLowerCase().includes(q) ||
            String(processMobileBySkill[s] || "").toLowerCase().includes(q)
        );
    }, [skillOptions, skillSearch, processMobileBySkill]);

    const toggleSkill = (name) => {
        setSelectedSkills((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    const onApplySkills = () => {
        setAppliedSkills(new Set(selectedSkills));
        setSkillOpen(false);
    };

    const sortedRows = useMemo(() => {
        return [...visibleRows].sort(
            (a, b) => (Number(b.total_calls) || 0) - (Number(a.total_calls) || 0)
        );
    }, [visibleRows]);

    const activeRows = useMemo(() => {
        return isPpc33 ? sortedRows : sortedRows.filter((r) => Number(r?.total_calls || 0) > 0);
    }, [sortedRows, isPpc33]);

    // ✅ Visible skills (cards)
    const visibleSkillRows = useMemo(() => {
        if (!appliedSkills || appliedSkills.size === 0) return activeRows;
        return activeRows.filter((r) => appliedSkills.has(String(r?.skillname || "").trim()));
    }, [activeRows, appliedSkills]);

    const yesterdayCallsBySkillHour = useMemo(() => {
        const map = new Map();
        visibleYesterdayRows.forEach((r) => {
            const skill = String(r?.skillname || "").trim();
            if (!skill) return;
            const hourMap = new Map();
            (r.hours || []).forEach((h) => {
                hourMap.set(Number(h?.hour || 0), Number(h?.calls || 0));
            });
            map.set(skill, hourMap);
        });
        return map;
    }, [visibleYesterdayRows]);

    const yesterdayCallsByHour = useMemo(() => {
        const map = new Map();
        const sourceHours = isPpc33 ? (visibleYesterdayAggregate?.hours || []) : (yesterdayHours || []);
        sourceHours.forEach((h) => {
            map.set(Number(h?.hour || 0), Number(h?.calls || 0));
        });
        return map;
    }, [isPpc33, visibleYesterdayAggregate, yesterdayHours]);

    // ✅ Is "All skills" selected?
    const isAllSkillsSelected = useMemo(() => {
        if (!skillOptions.length) return true;
        if (!appliedSkills || appliedSkills.size === 0) return true;
        return appliedSkills.size === skillOptions.length;
    }, [appliedSkills, skillOptions]);

    // ✅ BUILD TOP TABLE + SUMMARY BASED ON SELECTED SKILLS
    const computedTop = useMemo(() => {
        // If all skills selected => use API summary/hours directly
        if (isAllSkillsSelected) {
            const source = isPpc33 ? visibleCurrentAggregate : { summary, hours };
            const tCalls = source.summary?.total_calls ?? 0;
            const tOrders = source.summary?.total_orders ?? 0;
            const tSpend = source.summary?.total_spend ?? 0;
            const tPerf = source.summary?.perf ?? (tCalls ? (tOrders / tCalls) * 100 : 0);
            const tPerCall = perCallCost(tSpend, tCalls);
            return {
                topSummary: { total_calls: tCalls, y_calls: isPpc33 ? (visibleYesterdayAggregate?.summary?.total_calls ?? 0) : yesterdaySummary?.total_calls ?? 0, total_orders: tOrders, total_spend: tSpend, perf: tPerf },
                topHours: (Array.isArray(source.hours) ? source.hours : []).map((h) => ({
                    ...h,
                    ycalls: (isPpc33 ? (visibleYesterdayAggregate?.hours || []) : (yesterdayHours || [])).find((yh) => Number(yh?.hour || 0) === Number(h?.hour || 0))?.calls || 0,
                })),
                topPerCall: tPerCall,
            };
        }

        // Else: sum selected skills hour-wise
        const map = new Map(); // hour -> {hour,calls,orders,spend}
        let tCalls = 0, yCalls = 0, tOrders = 0, tSpend = 0;

        (visibleSkillRows || []).forEach((r) => {
            const skill = String(r?.skillname || "").trim();
            const yHourMap = yesterdayCallsBySkillHour.get(skill) || new Map();
            (r.hours || []).forEach((h) => {
                const hr = Number(h?.hour || 0);
                if (!hr) return;
                const prev = map.get(hr) || { hour: hr, calls: 0, ycalls: 0, orders: 0, spend: 0, cpc_spend: 0, cpc_clicks: 0, perf: 0 };
                prev.calls += Number(h?.calls || 0);
                prev.ycalls += Number(yHourMap.get(hr) || 0);
                prev.orders += Number(h?.orders || 0);
                prev.spend += Number(h?.spend || 0);
                prev.cpc_spend += Number(h?.cpc_spend || 0);
                prev.cpc_clicks += Number(h?.cpc_clicks || 0);
                map.set(hr, prev);
            });
        });

        const topHours = Array.from(map.values())
            .sort((a, b) => a.hour - b.hour)
            .map((x) => ({
                ...x,
                spend: Number(x.spend || 0),
                cpc: Number(x.cpc_clicks || 0) > 0 ? round2(Number(x.cpc_spend || 0) / Number(x.cpc_clicks || 0)) : 0,
                perf: Number(x.calls || 0) > 0 ? round1((Number(x.orders || 0) / Number(x.calls || 0)) * 100) : 0,
            }));

        topHours.forEach((x) => {
            tCalls += Number(x.calls || 0);
            yCalls += Number(x.ycalls || 0);
            tOrders += Number(x.orders || 0);
            tSpend += Number(x.spend || 0);
        });

        const tPerf = tCalls > 0 ? round1((tOrders / tCalls) * 100) : 0;
        const tPerCall = perCallCost(tSpend, tCalls);

        return {
            topSummary: { total_calls: tCalls, y_calls: yCalls, total_orders: tOrders, total_spend: round2(tSpend), perf: tPerf },
            topHours,
            topPerCall: tPerCall,
        };
    }, [isAllSkillsSelected, summary, hours, visibleSkillRows, yesterdaySummary, yesterdayCallsByHour, yesterdayCallsBySkillHour, isPpc33, visibleCurrentAggregate, visibleYesterdayAggregate, yesterdayHours]);

    function round1(n) {
        return Math.round(Number(n || 0) * 10) / 10;
    }
    function round2(n) {
        return Math.round(Number(n || 0) * 100) / 100;
    }

    const rangeTitle = useMemo(() => {
        const sh = meta?.start_hour ?? appliedRange.start;
        const eh = meta?.end_hour ?? appliedRange.end;
        const dt = meta?.date ?? appliedDate;
        if (!sh || !eh) return "Hourly Report";
        return `Hourly Report (${dt}) (${sh}–${eh})`;
    }, [meta, appliedRange, appliedDate]);

    const isPendingChange = useMemo(() => {
        return (
            Number(startHour) !== Number(appliedRange.start) ||
            Number(endHour) !== Number(appliedRange.end) ||
            String(selectedDate) !== String(appliedDate)
        );
    }, [startHour, endHour, appliedRange, selectedDate, appliedDate]);

    const skillButtonText = useMemo(() => {
        if (!appliedSkills || appliedSkills.size === 0) return "All";
        if (appliedSkills.size === skillOptions.length) return "All";
        if (appliedSkills.size === 1) {
            const skill = Array.from(appliedSkills)[0];
            const mobile = processMobileBySkill[skill] || "";
            return showProcessMobile && mobile ? mobile : skill;
        }
        return `${appliedSkills.size} Selected`;
    }, [appliedSkills, skillOptions, processMobileBySkill, showProcessMobile]);

    // ✅ cell classes
    const perfClass = (perfVal) => {
        const p = Number(perfVal || 0);
        return p > PERF_GREEN_ABOVE ? "hlPerfGood" : "";
    };

    const costClass = (costVal) => {
        const c = Number(costVal || 0);
        return c > COST_RED_ABOVE ? "hlCostBad" : "";
    };

    // ✅ skill header stats (calls/orders/spend/perf/percall)
    const getSkillHeaderStats = (r) => {
        const skill = String(r?.skillname || "").trim();
        const calls = Number(r?.total_calls || 0);
        const ycalls = Array.from((yesterdayCallsBySkillHour.get(skill) || new Map()).values()).reduce(
            (sum, v) => sum + Number(v || 0),
            0
        );
        const orders = Number(r?.total_orders || 0);
        const spend = Number(r?.total_spend || 0);
        const perf = calls > 0 ? round1((orders / calls) * 100) : 0;
        const pc = perCallCost(spend, calls);
        return { calls, ycalls, orders, spend, perf, pc };
    };

    return (
        <div className="misDashboard today-live-call-page">
            <Helmet>
                <title>SATKARTAR::HOURLY REPORT</title>
            </Helmet>

            <style>{`
        .mini-table{ table-layout: fixed; width:100%; }
        .mini-table th,.mini-table td{font-size:12px; padding:6px 8px; vertical-align:middle;}
        .skillTableWrap{max-height:260px;overflow:auto;-webkit-overflow-scrolling:touch;}
        .skillCardHeader{display:flex;gap:10px;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;}
        .groupname{font-weight:700;color:#ff4a32;line-height:1.25;max-width:100%;overflow-wrap:anywhere;word-break:break-word;}
        .skillCardStats{font-size:12px;text-align:right;white-space:normal;line-height:1.45;margin-left:auto;}
        .filterRow{display:flex;gap:8px;align-items:end;flex-wrap:wrap;}
        .filterBox{display:flex;flex-direction:column;gap:4px;}
        .filterBox label{font-weight:600;font-size:12px;margin:0;}
        .pending{font-size:12px;color:#ff7a00;font-weight:600;}

        /* ✅ highlight rules */
        .hlPerfGood{
          background:#e8f5e9 !important;
          color:#2e7d32 !important;
          font-weight:800 !important;
        }
        .hlCostBad{
          background:#ffebee !important;
          color:#b71c1c !important;
          font-weight:800 !important;
        }

        /* ✅ skeleton shimmer */
        .sk{position:relative; overflow:hidden; background:#eee; border-radius:6px;}
        .sk:after{
          content:""; position:absolute; inset:0; transform:translateX(-100%);
          background:linear-gradient(90deg,transparent,rgba(255,255,255,.65),transparent);
          animation:shimmer 1.2s infinite;
        }
        @keyframes shimmer{100%{transform:translateX(100%)}}

        /* ✅ Skill dropdown */
        .skillDropWrap{position:relative; min-width:260px;}
        .skillDropBtn{
          width:260px;
          display:flex; align-items:center; justify-content:space-between;
          padding:6px 10px;
          border:1px solid rgba(0,0,0,.15);
          border-radius:6px;
          background:#fff;
          font-size:13px;
        }
        .skillDropMenu{
          position:absolute; top:100%; left:0; z-index:50;
          width:260px;
          background:#fff;
          border:1px solid rgba(0,0,0,.15);
          border-radius:10px;
          margin-top:6px;
          box-shadow:0 10px 30px rgba(0,0,0,.12);
          overflow:hidden;
        }
        .skillDropHead{padding:10px; border-bottom:1px solid rgba(0,0,0,.08);}
        .skillSearch{
          width:100%;
          border:1px solid rgba(0,0,0,.15);
          border-radius:8px;
          padding:7px 10px;
          font-size:13px;
          outline:none;
        }
        .skillActions{
          display:flex; padding:10px;
          border-bottom:1px solid rgba(0,0,0,.08);
          justify-content:space-between;
        }
        .skillList{max-height:240px; overflow:auto; padding:8px 5px;}
        .skillItem{display:flex; align-items:center; gap:10px; padding:6px 2px; font-size:13px;}
        .skillFoot{
          display:flex; gap:8px; justify-content:space-between;
          padding:10px;
          border-top:1px solid rgba(0,0,0,.08);
          background:#fafafa;
        }
        @media (max-width:991px){
          .filterRow{align-items:stretch;}
          .filterBox{flex:1 1 160px;min-width:0;}
          .skillFilterBox{flex-basis:260px;}
          .skillDropWrap,.skillDropBtn,.skillDropMenu{width:100%;min-width:0;}
          .livecallcount{font-size:14px;line-height:1.5;}
          .skillCardStats{text-align:left;margin-left:0;width:100%;}
        }
        @media (max-width:575px){
          .filterRow{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
          .filterBox{width:100%;}
          .filterBox:first-child,.skillFilterBox,.btnskillapply,.btnskill{grid-column:1 / -1;width:100%;}
          .skillDropMenu{position:static;margin-top:6px;}
          .mini-table th,.mini-table td{font-size:11px;padding:7px 8px;}
          .card-body.p-2{padding:8px !important;}
        }
      `}</style>

            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />

                        <div className="live-report-head mt-2">
                            <div>
                                <p className="live-report-kicker">
                                    Hourly PPC Call View
                                    {!isAllSkillsSelected && (
                                        <span style={{ marginLeft: 8 }}>(Filtered: {skillButtonText})</span>
                                    )}
                                    {lastUpdatedAt && (
                                        <span style={{ marginLeft: 8 }}>
                                            Updated: {lastUpdatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                        </span>
                                    )}
                                </p>
                                <h5 className="livecallcount mb-0">{rangeTitle}</h5>

                                {isPendingChange && !isLoading && (
                                    <div className="pending">
                                        Filters changed (not applied). Click Apply to load {selectedDate} | {startHour}–{endHour}.
                                    </div>
                                )}
                            </div>
                            <div className="live-summary-strip">
                                <span className="metric-card metric-calls"><i>T</i><em>TCalls</em><b>{isLoading ? "..." : fmt(computedTop.topSummary.total_calls)}</b></span>
                                <span className="metric-card metric-calls"><i>Y</i><em>YCalls</em><b>{isLoading ? "..." : fmt(computedTop.topSummary.y_calls)}</b></span>
                                <span className="metric-card metric-orders"><i>O</i><em>Orders</em><b>{isLoading ? "..." : fmt(computedTop.topSummary.total_orders)}</b></span>
                                <span className="metric-card metric-spend"><i>S</i><em>Spend</em><b>{isLoading ? "..." : numberFormatWithGst(computedTop.topSummary.total_spend)}</b></span>
                                <span className="metric-card metric-cost"><i>P</i><em>Per Call</em><b className={computedTop.topPerCall > COST_RED_ABOVE ? "is-danger" : ""}>{isLoading ? "..." : numberFormatWithGst(computedTop.topPerCall)}</b></span>
                                <span className="metric-card metric-perf"><i>%</i><em>Perf</em><b>{isLoading ? "..." : `${Number(computedTop.topSummary.perf || 0).toFixed(1)}%`}</b></span>
                            </div>
                        </div>

                        {/* ✅ FILTERS */}
                        <div className="card live-filter-card mt-2">
                            <div className="card-body p-2">
                                <div className="filterRow">
                                    {/* ✅ Date */}
                                    <div className="filterBox">
                                        <label>Date</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={selectedDate}
                                            onChange={(e) => setSelectedDate(e.target.value)}
                                            disabled={isLoading}
                                        />
                                    </div>

                                    <div className="filterBox">
                                        <label>From Hour</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={startHour}
                                            onChange={(e) => setStartHour(Number(e.target.value))}
                                            disabled={isLoading}
                                        >
                                            {HOURS.map((h) => (
                                                <option key={h} value={h}>{h}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="filterBox">
                                        <label>To Hour</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={endHour}
                                            onChange={(e) => setEndHour(Number(e.target.value))}
                                            disabled={isLoading}
                                        >
                                            {HOURS.map((h) => (
                                                <option key={h} value={h}>{h}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* ✅ Skill multi-select */}
                                    <div className="filterBox skillFilterBox" ref={skillRef}>
                                        <label>Process Name</label>
                                        <div className="skillDropWrap">
                                            <button
                                                type="button"
                                                className="skillDropBtn"
                                                onClick={() => setSkillOpen((s) => !s)}
                                                disabled={isLoading || !skillOptions.length}
                                            >
                                                <span style={{ fontWeight: 600 }}>{skillButtonText}</span>
                                                <span style={{ opacity: 0.7 }}>▾</span>
                                            </button>

                                            {skillOpen && (
                                                <div className="skillDropMenu">
                                                    <div className="skillDropHead">
                                                        <input
                                                            className="skillSearch"
                                                            value={skillSearch}
                                                            onChange={(e) => setSkillSearch(e.target.value)}
                                                            placeholder="Search skill..."
                                                        />
                                                    </div>

                                                    <div className="skillList">
                                                        {filteredSkillOptions.length ? (
                                                            filteredSkillOptions.map((name) => (
                                                                <label key={name} className="skillItem">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedSkills.has(name)}
                                                                        onChange={() => toggleSkill(name)}
                                                                    />
                                                                    <span>{getSkillDisplay(name)}</span>
                                                                </label>
                                                            ))
                                                        ) : (
                                                            <div className="text-muted" style={{ fontSize: 13, padding: "8px 2px" }}>
                                                                No skills found
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="skillFoot">
                                                        <button type="button" className="btnresetall" onClick={onApplySkills} style={{ width: "100%" }}>
                                                            Apply Skills
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <button className="btnskillapply" onClick={onApply} disabled={isLoading}>
                                        Apply
                                    </button>

                                    <button className="btnskillapply" onClick={onRefresh} disabled={isLoading}>
                                        Refresh
                                    </button>

                                    <button className="btnskill" onClick={() => setShowSkills((s) => !s)} disabled={isLoading}>
                                        {showSkills ? "Hide Skill View" : "Show Skill View"}
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* ✅ TOP Hour table (NOW reflects selected skill also) */}
                <div className="row">
                    <div className="col-12">
                        <div className="live-center-card">
                            <div className="live-center-card-head">
                                <div className="sh-left">Hour View</div>
                                <div className="live-center-metrics">
                                    <span className="metric-card metric-calls"><i>T</i><em>TCalls</em><b>{fmt(computedTop.topSummary.total_calls)}</b></span>
                                    <span className="metric-card metric-calls"><i>Y</i><em>YCalls</em><b>{fmt(computedTop.topSummary.y_calls)}</b></span>
                                    <span className="metric-card metric-orders"><i>O</i><em>Orders</em><b>{fmt(computedTop.topSummary.total_orders)}</b></span>
                                    <span className="metric-card metric-cost"><i>P</i><em>Per Call</em><b className={computedTop.topPerCall > COST_RED_ABOVE ? "is-danger" : ""}>{numberFormatWithGst(computedTop.topPerCall)}</b></span>
                                </div>
                            </div>
                            <div className="live-table-scroll">
                            <table className="table mb-0 mini-table live-skill-table table-bordred">
                                <thead className="stickyHead">
                                    <tr className="colHead">
                                        <th style={{ width: 90, textAlign: "right" }}>Hour</th>
                                        <th style={{ width: 110, textAlign: "right" }}>TCalls</th>
                                        <th style={{ width: 110, textAlign: "right" }}>YCalls</th>
                                        <th style={{ width: 120, textAlign: "right" }}>Orders</th>
                                        <th style={{ width: 140, textAlign: "right" }}>Spend</th>
                                        <th style={{ width: 110, textAlign: "right" }}>CPC</th>
                                        <th style={{ width: 140, textAlign: "right" }}>Per Call</th>
                                        <th style={{ width: 100, textAlign: "right" }}>Perf</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <HourlyTableSkeleton rows={Math.max(4, (computedTop.topHours?.length || 0) || 6)} />
                                    ) : computedTop.topHours?.length ? (
                                        computedTop.topHours.map((h, idx) => {
                                            const pc = perCallCost(h.spend, h.calls);
                                            return (
                                                <tr key={idx}>
                                                    <td style={{ textAlign: "right" }}>{h.hour}</td>
                                                    <td style={{ textAlign: "right" }}>{fmt(h.calls)}</td>
                                                    <td style={{ textAlign: "right" }}>{fmt(h.ycalls)}</td>
                                                    <td style={{ textAlign: "right" }}>{fmt(h.orders)}</td>
                                                    <td style={{ textAlign: "right" }}>{numberFormatWithGst(h.spend)}</td>
                                                    <td style={{ textAlign: "right" }}>{numberFormatWithGst(h.cpc)}</td>
                                                    <td style={{ textAlign: "right" }} className={costClass(pc)}>
                                                        {numberFormatWithGst(pc)}
                                                    </td>
                                                    <td style={{ textAlign: "right" }} className={perfClass(h.perf)}>
                                                        {Number(h.perf || 0).toFixed(1)}%
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={8} className="text-center">
                                                No hourly data in selected filters
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ✅ Skill-wise hourly view */}
                {showSkills && (
                    <div className="row mt-3">
                        {isLoading ? (
                            <>
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <SkillCardSkeleton key={`csk-${i}`} />
                                ))}
                            </>
                        ) : (
                            visibleSkillRows.map((r, idx) => {
                                const hs = getSkillHeaderStats(r);
                                return (
                                    <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={idx}>
                                        <div className="live-center-card">
                                                <div className="live-center-card-head">
                                                    <div className="sh-left">
                                                        {getSkillDisplay(r)}
                                                    </div>

                                                    {/* ✅ Updated header with Perf + PerCall highlight */}
                                                    <div className="live-center-metrics">
                                                        <span className="metric-card metric-calls"><i>T</i><em>TCalls</em><b>{fmt(hs.calls)}</b></span>
                                                        <span className="metric-card metric-calls"><i>Y</i><em>YCalls</em><b>{fmt(hs.ycalls)}</b></span>
                                                        <span className="metric-card metric-orders"><i>O</i><em>Orders</em><b>{fmt(hs.orders)}</b></span>
                                                        <span className="metric-card metric-spend"><i>S</i><em>Spend</em><b>{numberFormatWithGst(hs.spend)}</b></span>
                                                        <span className="metric-card metric-cost"><i>P</i><em>Per Call</em><b className={hs.pc > COST_RED_ABOVE ? "is-danger" : ""}>{numberFormatWithGst(hs.pc)}</b></span>
                                                    </div>
                                                </div>

                                                <div className="live-table-scroll">
                                                    <table className="table mb-0 mini-table live-skill-table table-bordred">
                                                        <thead className="stickyHead">
                                                            <tr className="colHead">
                                                                <th style={{ textAlign: "right" }}>Hour</th>
                                                                <th style={{ textAlign: "right" }}>TCalls</th>
                                                                <th style={{ textAlign: "right" }}>YCalls</th>
                                                                <th style={{ textAlign: "right" }}>Orders</th>
                                                                <th style={{ textAlign: "right" }}>Spend</th>
                                                                <th style={{ textAlign: "right" }}>CPC</th>
                                                                <th style={{ textAlign: "right" }}>Per Call</th>
                                                                <th style={{ textAlign: "right" }}>Perf</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(r.hours || []).length ? (
                                                                (r.hours || []).map((h, i2) => {
                                                                    const pc = perCallCost(h.spend, h.calls);
                                                                    return (
                                                                        <tr key={i2}>
                                                                            <td style={{ textAlign: "right" }}>{h.hour}</td>
                                                                            <td style={{ textAlign: "right" }}>{fmt(h.calls)}</td>
                                                                            <td style={{ textAlign: "right" }}>{fmt((yesterdayCallsBySkillHour.get(String(r?.skillname || "").trim()) || new Map()).get(Number(h?.hour || 0)) || 0)}</td>
                                                                            <td style={{ textAlign: "right" }}>{fmt(h.orders)}</td>
                                                                            <td style={{ textAlign: "right" }}>{numberFormatWithGst(h.spend)}</td>
                                                                            <td style={{ textAlign: "right" }}>{numberFormatWithGst(h.cpc)}</td>
                                                                            <td style={{ textAlign: "right" }} className={costClass(pc)}>
                                                                                {numberFormatWithGst(pc)}
                                                                            </td>
                                                                            <td style={{ textAlign: "right" }} className={perfClass(h.perf)}>
                                                                                {Number(h.perf || 0).toFixed(1)}%
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan={8} className="text-center">
                                                                        No skill hourly data
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>

                                            </div>
                                        </div>
                                );
                            })
                        )}

                        {!isLoading && (!visibleSkillRows || visibleSkillRows.length === 0) && (
                            <div className="col-12">
                                <div className="alert alert-info mb-0">
                                    No skill-wise hourly data for selected skills.
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </ContentWrapper>
        </div>
    );
};

export default withAuth(HourlyPPCTodayReport);
